// Inferenza degli eventi di una partita Fantacalcio a partire dal delta fantavoto - voto_base.
//
// Il mapping bonus/malus segue le regole standard del Fantacalcio Serie A:
//   +3   gol attaccante · +3.5 gol difensore/centrocampista · +6 gol portiere
//   +3   rigore segnato · -3 rigore sbagliato · +3 rigore parato (P)
//   +1   assist          · -0.5 ammonizione · -1 espulsione o gol subito (P)
//   -2   autogol
//
// Impossibile decodificare univocamente ogni combinazione dal solo delta, ma le
// combinazioni frequenti (1 evento) sono riconoscibili. Restituiamo la lista di
// icone che meglio spiegano il delta osservato — pensata per essere renderizzata
// sotto il nome del giocatore nel breakdown partita, con lo stesso look
// dell'app Fantacalcio.it.

const EPS = 0.01;

// Icone: colore di fondo (bg) + emoji-glifo. Font-color scelto per contrasto.
const ICONS = {
  goal:          { key: "goal",          label: "Gol",              emoji: "⚽", bg: "#16a34a" },
  own_goal:      { key: "own_goal",      label: "Autogol",          emoji: "⚽", bg: "#dc2626" },
  penalty_scored:{ key: "penalty_scored",label: "Rigore segnato",   emoji: "🎯", bg: "#16a34a" },
  penalty_miss:  { key: "penalty_miss",  label: "Rigore sbagliato", emoji: "🎯", bg: "#dc2626" },
  penalty_saved: { key: "penalty_saved", label: "Rigore parato",    emoji: "🧤", bg: "#16a34a" },
  yellow:        { key: "yellow",        label: "Ammonizione",      emoji: "🟨", bg: "#facc15" },
  red:           { key: "red",           label: "Espulsione",       emoji: "🟥", bg: "#dc2626" },
  goal_conceded: { key: "goal_conceded", label: "Gol subito",       emoji: "🥅", bg: "#94a3b8" },
  assist:        { key: "assist",        label: "Assist",           emoji: "👟", bg: "#3b82f6" },
};

const ROLE_MAP = (r) => {
  const x = String(r || "").trim().toUpperCase();
  if (x === "P" || x === "POR" || x === "PT") return "P";
  if (x === "D" || x === "DIF" || x === "DEF") return "D";
  if (x === "C" || x === "CEN") return "C";
  if (x === "A" || x === "ATT") return "A";
  return null;
};

/**
 * Inferisce gli eventi di un giocatore dal delta fantavoto - voto_base.
 * @param {string} role — P/D/C/A o POR/DIF/CEN/ATT
 * @param {number|null|undefined} votoBase
 * @param {number|null|undefined} fantavoto
 * @returns {{key:string,label:string,emoji:string,bg:string}[]}
 */
export function inferPlayerEvents(role, votoBase, fantavoto) {
  const r = ROLE_MAP(role);
  const vb = Number(votoBase);
  const fv = Number(fantavoto);
  if (!Number.isFinite(vb) || !Number.isFinite(fv)) return [];
  let delta = Math.round((fv - vb) * 100) / 100; // arrotondato al centesimo
  const events = [];

  // 1) Ammonizione — -0.5 (frequente e sempre uguale)
  //    Applichiamo prima per estrarla dal delta e semplificare l'analisi restante.
  if (Math.abs(delta + 0.5) < EPS || Math.abs(delta + 0.5 - Math.round(delta + 0.5)) < EPS) {
    // Fallback semplice: se il delta include chiaramente -0.5, marchiamo yellow.
  }
  // Detezione tipica per singolo evento
  if (Math.abs(delta - 0) < EPS) return events; // nessun evento

  // Delta positivi
  if (delta >= 3 - EPS) {
    // Gol o rigore segnato. Combinazioni frequenti:
    //  +3       → gol A o rigore segnato (indistinguibile → mostro solo goal)
    //  +3.5     → gol D/C
    //  +2.5     → gol A -0.5 ammon.
    //  +3.0 (D) → gol D con -0.5 ammon. contemporaneo? no: sarebbe +3
    //  +6       → gol P (raro)
    if (r === "P" && delta >= 5.5 - EPS) {
      events.push(ICONS.goal);
      delta -= 6;
    } else if ((r === "D" || r === "C") && delta >= 3.5 - EPS) {
      events.push(ICONS.goal);
      delta -= 3.5;
    } else {
      events.push(ICONS.goal);
      delta -= 3;
    }
  }
  if (delta >= 1 - EPS && delta < 3 - EPS) {
    events.push(ICONS.assist);
    delta -= 1;
  }
  if (delta > 0 - EPS && delta < 1 - EPS && delta > 0.4) {
    // +0.5 secco: potrebbe essere +1 assist -0.5 ammon.
    events.push(ICONS.assist);
    events.push(ICONS.yellow);
    delta = 0;
  }

  // Delta negativi
  if (delta <= -3 + EPS) {
    events.push(ICONS.penalty_miss);
    delta += 3;
  }
  if (delta <= -2 + EPS && delta > -3 + EPS) {
    events.push(ICONS.own_goal);
    delta += 2;
  }
  if (delta <= -1 + EPS && delta > -2 + EPS) {
    // Portiere: gol subito. Non-portiere: espulsione.
    events.push(r === "P" ? ICONS.goal_conceded : ICONS.red);
    delta += 1;
  }
  if (delta <= -0.5 + EPS && delta > -1 + EPS) {
    events.push(ICONS.yellow);
    delta += 0.5;
  }

  return events;
}

export const EVENT_META = ICONS;
